import React, { useState, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import UploadRulebook from "./UploadRulebook";
import ManageRules from "./ManageRules";
import UploadPayroll from "./UploadPayroll";
import { usePayrollUploadLoader } from "../common/loader/PayrollUploadLoaderContext";
import ViewValidationResult from "./ViewValidationResult";
import { useNavigate } from "react-router-dom";
import { analyzeRule, getInstructions, uploadRulebook, uploadPayrollFiles, getNotification } from "../services/managerules.service";
import { useLoader } from "../common/LoaderContext";
import { proceedManageRule } from "../services/managerules.service";
import { useSnackbar } from "../common/SnackbarContext";
import CircularProgress from '@mui/material/CircularProgress';
import { useNotification } from "../common/NotificationContext";
import { useLocation } from "react-router-dom";

const steps = [
  "Upload Rulebook",
  "Manage Rules",
  "Upload Payroll"
];



const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const RulePage = () => {
  const navigate = useNavigate();
  //const [activeStep, setActiveStep] = React.useState(0);
  const { showSnackbar } = useSnackbar();
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>(
    {}
  );
  //const [instructions, setInstruction] = React.useState();
  const { showPayloadUploadLoader, hidePayloadUploadLoader } = usePayrollUploadLoader();
  const { showLoader, hideLoader } = useLoader();

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const [payrollUploading, setPayrollUploading] = React.useState(false);
  const { addNotification } = useNotification();
  const [pollingId, setPollingId] = useState<NodeJS.Timeout | null>(null);
  const [manageRuleInstructions,setManageRuleInstructions] = useState("");

  const handleGotoDashboard = async () => {
     // Route to the /about page

    try {
      // ();
      showLoader('reValidate');
      const response = await proceedManageRule();
      if (response) {
        
        navigate('/dashboard');
        hideLoader();
      }
    } catch (error:any) {
      hideLoader();
      console.error("Re-validation error:", error);
      showSnackbar(error['response'].data.detail, "error");
      return false;
    }

  };
  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const location = useLocation();
  const goToManageRules = location.state?.goToManageRules || false;

  const [activeStep, setActiveStep] = React.useState<number>(
    location.state?.activeStep ?? (goToManageRules ? 1 : 0)
  );
  const [instructions, setInstruction] = React.useState();

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        showLoader("instructions");
        const res = await getInstructions();
        setInstruction(res);
        hideLoader();
      } catch (err:any) {
        console.error("Failed to fetch instructions:", err);
        setManageRuleInstructions(err.response.data.detail);
        hideLoader();
      }
    };

    if (goToManageRules || location.state?.activeStep === 1) {
      fetchInstructions();
    }
  }, [goToManageRules]);

  const handleUpload = async (): Promise<boolean> => {

    const fileInputs = [
      document.getElementById("rulespriorities") as HTMLInputElement,
      document.getElementById("wagetype") as HTMLInputElement,
      document.getElementById("payroll") as HTMLInputElement,
    ];

    if (!fileInputs[0]?.files?.[0] || !fileInputs[1]?.files?.[0] || !fileInputs[2]?.files?.[0]) {
      showSnackbar("Please upload all files before proceeding", "error");
      return false;
    }
    const formData = new FormData();
    formData.append("rules_file", fileInputs[0]?.files?.[0] || "");
    formData.append("wt_description_file", fileInputs[1]?.files?.[0] || "");
    formData.append("payroll_schema_file", fileInputs[2]?.files?.[0] || "");

    try {
      // ();
      showLoader('validate');
      const response = await uploadRulebook(formData);

      // const data = await response;

      if (null != response.upload_id || "" != response.upload_id) {
        hideLoader();
        // showSnackbar(response.upload_id, "success");
        // hidePayloadUploadLoader();
        const payload = {
          "upload_id": response.upload_id,
          "rules_file": response.rules_file,
          "wt_description_file": response.wt_description_file,
          "payroll_schema_file": response.payroll_schema_file
        }
        showLoader('analyze');
        if (response.Warnings?.length) {
          showLoader('analyze',response.Warnings.join(", "));
        }
        const analyze_response = await analyzeRule(payload);
        if ("File Analysis Succesfully Completed" == analyze_response.message) {
          const get_instructions = await getInstructions();
          setInstruction(get_instructions);
        } else {
          showSnackbar(analyze_response?.detail || analyze_response?.message || "Analysis failed", "error");
          hideLoader();
          return false;
        }
        hideLoader();
        return true;
      } else {

        showSnackbar(
          "Upload failed: " + (response.message || "Unknown error"),
          "error"
        );
        hideLoader();
        return false;
      }
    } catch (error: any) {
      hideLoader();
      console.error("Upload error:", error);
      const errMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while uploading the files.";
      showSnackbar(errMsg, "error");
      return false;
    }
  };

  const handleCompleteStep0 = async () => {
    const success = await handleUpload();
    if (success) {
      handleComplete();
    }
  };

  const handleUploadPayroll = async () => {
    setPayrollUploading(true);
    try {
      const payrollInput = document.getElementById("rulespriorities") as HTMLInputElement;
      const timeInput = document.getElementById("wagetype") as HTMLInputElement;

      if (!payrollInput?.files?.[0]) {
        showSnackbar("Payroll Variance Data file is mandatory", "error");
        return false;
      }

      const formData = new FormData();
      formData.append("payroll_variance_file", payrollInput.files[0]);
      if (timeInput?.files?.[0]) {
        formData.append("time_data_file", timeInput.files[0]);
      }

      // showPayloadUploadLoader("Validating payroll files...");
      const response = await uploadPayrollFiles(formData);

      if (response?.notification_id) {
        showPayloadUploadLoader("Validating payroll files...");
        if (response.warning?.length) {
          showPayloadUploadLoader(response.warning.join(", "));
        }
        const intervalId = setInterval(async () => {
          try {
            const notiRes = await getNotification(response.notification_id);
            const status = notiRes.notifications?.[0]?.status || "";
            const session = notiRes.notifications?.[0]?.session;
            const docName = notiRes.notifications?.[0]?.doc_name;
            if (status.toLowerCase().includes("successfully validated")) {
              addNotification({
                id: response.notification_id,
                message: status,
                read: false,
                session:session,
                docName:docName,
                status: "success",
                clickable: true,
              });
              const event = new CustomEvent("newNotification", { detail: { status: "success" } });
              window.dispatchEvent(event);
              clearInterval(intervalId);
            } else if (status.toLowerCase().includes("failed")) {
              addNotification({
                id: response.notification_id,
                message: "Payroll validation failed. Please check logs file on Dashboard",
                read: false,
                status: "fail",
                session:session,
                docName:docName,
                clickable: false,
              });
              const event = new CustomEvent("newNotification", { detail: { status: "failed" } });
              window.dispatchEvent(event);
              clearInterval(intervalId);
            }
          } catch (err) {
            console.error("Polling error:", err);
            clearInterval(intervalId);
          }
        }, 10000);
        setPollingId(intervalId);
        handleComplete();
      } else {
        showSnackbar(
          "Upload failed: " + (response?.message || "Unknown error"),
          "error"
        );
        return false;
      }
    } catch (error:any) {
       setPayrollUploading(false);
      console.error("Payroll upload error:", error['response'].data.detail);
      showSnackbar(error['response'].data.detail, "error");
      return false;
    } finally {
      setPayrollUploading(false);
    }
  };

  // const handleUploadPayroll = async () => {
  //   const success = await handleUpload();
  //   if (success) {
  //     handleComplete();
  //     showPayloadUploadLoader();
  //   }
  // }

  const handleCompleteStep1 = async () => {
    try {
      // ();
      showLoader('reValidate');
      const response = await proceedManageRule();
      if (response) {
        handleComplete();
        hideLoader();
      }
    } catch (error:any) {
      hideLoader();
      console.error("Re-validation error:", error);
      showSnackbar(error['response'].data.detail, "error");
      return false;
    }
  };

  return (
    <Box sx={{ width: "100%" }} className="stepper-div">
      <Stepper nonLinear activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton color="inherit" onClick={handleStep(index)}
              disabled={index > activeStep || (index !== activeStep && !completed[index])}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <div>
        {allStepsCompleted() ? (
          <>
            <Typography sx={{ mt: 2, mb: 1 }}>
              All steps completed - you&apos;re finished
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button onClick={handleReset}>Reset</Button>
            </Box>
          </>
        ) : (
          <>
            <Box>
              {activeStep === 0 && (
                <>
                  <UploadRulebook onUpload={handleUpload} />
                  <div className="page-footer">
                    <Button
                      className="footer-btn"
                      variant="contained"
                      onClick={handleCompleteStep0}
                      sx={{ textTransform: "none", fontFamily: "Inter",backgroundColor:'#007CB0' }}
                    >
                      Proceed
                    </Button>
                  </div>
                </>
              )}

              {activeStep === 1 && (
                <>
                  {instructions ? (
                    <ManageRules data={instructions} />
                  ) : (
                    <Typography sx={{ color: "#fff", padding: "16px" }}>
                      {manageRuleInstructions}
                    </Typography>
                  )}
                  <div className="page-footer">
                    <Button
                      variant="outlined"
                      onClick={handleGotoDashboard}
                      sx={{ textTransform: "none", marginRight: "10px",color:'#007CB0',borderColor:'#007CB0' }}
                    >
                      Go to Dashboard
                    </Button>
                    
                    <Button
                      className="footer-btn"
                      variant="contained"
                      disabled={manageRuleInstructions!=""}
                      onClick={handleCompleteStep1}
                      sx={{ textTransform: "none", fontFamily: "Inter",backgroundColor:'#007CB0' }}
                    >
                      Proceed
                    </Button>
                  </div>
                </>
              )}
              {activeStep === 2 && (
                <>
                  <UploadPayroll onUpload={handleUpload} />
                  <div className="page-footer">
                    <Button
                      className="footer-btn"
                      variant="contained"
                      onClick={handleUploadPayroll}
                      sx={{ textTransform: "none", fontFamily: "Inter",backgroundColor:'#007CB0' }}
                      disabled={payrollUploading}
                    >
                      {payrollUploading ? (
                        <CircularProgress size={24} style={{color:"#fff"}} />
                      ) : (
                        "Proceed"
                      )}
                    </Button>
                  </div>
                </>
              )}
              {/* {activeStep === 3 && (
                <>
                  <ViewValidationResult />
                  <div className="page-footer">
                    <Button
                      className="footer-btn"
                      variant="contained"
                      onClick={handleGotoDashboard}
                      sx={{ textTransform: "none", fontFamily: "Inter" }}
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                </>
              )} */}
            </Box>

            {activeStep === 5 && (
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button color="inherit" onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>

                <Box sx={{ flex: "1 1 auto" }}>
                  <Button onClick={handleNext} sx={{ mr: 1 }}>
                    Next
                  </Button>
                  {activeStep !== steps.length &&
                    (completed[activeStep] ? (
                      <Typography
                        variant="caption"
                        sx={{ display: "inline-block" }}
                      >
                        Step {activeStep + 1} already completed
                      </Typography>
                    ) : (
                      <Button onClick={handleComplete}>
                        {completedSteps() === totalSteps() - 1
                          ? "Finish"
                          : "Complete Step"}
                      </Button>
                    ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </div>


    </Box>
  );
};

export default RulePage;

